import { Badge, Group, type MantineColor, Stack, Text, ThemeIcon, Tooltip } from 'ui';
import { IconInfo, IconTag } from 'ui/Icons';

interface OrderLineItemBaseProps {
  /**
   * Label for the line item
   */
  label: string;
  /**
   * If the label should be bold
   */
  labelBold?: boolean;
  /**
   * ReactNode to display after the label
   */
  labelAfter?: React.ReactNode;

  /**
   * Value for the line item
   */
  value?: string;
  /**
   * If the value should be bold
   */
  valueBold?: boolean;

  /**
   * Information to display in a tooltip icon, will show up instead of value if provided
   */
  info?: string;
  /**
   * Color for the label
   */
  labelColor?: MantineColor;
  /**
   * Color for the value
   */
  valueColor?: MantineColor;
}

const OrderLineItemBase = ({
  label,
  value,
  labelBold,
  valueBold,
  labelAfter,
  info,
  labelColor = 'gray.8',
  valueColor = 'gray.8',
}: OrderLineItemBaseProps) => {
  return (
    <Group justify="space-between" w="100%" align="flex-start">
      <Group gap="xs" align="flex-start">
        <Text fz="sm" fw={labelBold ? 600 : undefined} c={labelColor}>
          {label}
        </Text>
        {labelAfter}
      </Group>
      {info ? (
        <Tooltip label={info} position="top-end">
          <IconInfo size={16} color="gray" />
        </Tooltip>
      ) : (
        <Text fz="sm" fw={valueBold ? 600 : undefined} c={valueColor}>
          {value}
        </Text>
      )}
    </Group>
  );
};

type OrderLineItemHeaderProps = Pick<OrderLineItemBaseProps, 'label' | 'info'>;

const OrderLineItemHeader = (props: OrderLineItemHeaderProps) => {
  return <OrderLineItemBase labelBold {...props} />;
};

type OrderLineItemLineItemProps = Required<Pick<OrderLineItemBaseProps, 'label' | 'value'>>;

const OrderLineItem = (props: OrderLineItemLineItemProps) => {
  return <OrderLineItemBase valueBold {...props} />;
};

const DiscountBadgeList = ({ couponCodes }: { couponCodes?: string[] }) => {
  if (!couponCodes) return null;
  return (
    <Stack gap="xs">
      {couponCodes.map((couponCode, index) => (
        <Badge
          type="neutral"
          leftSection={
            <ThemeIcon variant="transparent" c="gray.8" size={12}>
              <IconTag />
            </ThemeIcon>
          }
          key={couponCode + index}
        >
          {couponCode}
        </Badge>
      ))}
    </Stack>
  );
};
interface OrderLineItemDiscountProps {
  /** Single coupon Code to display in the badge list, if provided, will override the couponCodes list */
  couponCode?: string;
  /** List of coupon Codes to display in the badge list */
  couponCodes?: string[];
  /** Cumulative discount amount */
  discountAmount: string;
}

const OrderLineItemDiscount = ({
  couponCodes,
  couponCode,
  discountAmount,
}: OrderLineItemDiscountProps) => {
  return (
    <OrderLineItemBase
      label="Discount"
      labelAfter={<DiscountBadgeList couponCodes={couponCode ? [couponCode] : couponCodes} />}
      value={discountAmount}
      valueBold
    />
  );
};

OrderLineItemBase.Header = OrderLineItemHeader;
OrderLineItemBase.Item = OrderLineItem;
OrderLineItemBase.Discount = OrderLineItemDiscount;

export { OrderLineItemBase as OrderLineItem, type OrderLineItemBaseProps as OrderLineItemProps };
